import { getState, patchState, signalStoreFeature, SignalStoreFeatureResult, watchState, withHooks, withMethods } from '@ngrx/signals';
import { EnvironmentService } from '../environment/environment.service';
import { inject } from '@angular/core';

type SelectFn<State> = (state: State) => Record<string, unknown>;
type ParseFn<State> = (data: object) => Partial<State>;

export function withWebStorageSync<Input extends SignalStoreFeatureResult>(config: {
  storage: Storage | undefined;
  prefix: string;
  storageKey: string;
  showPortalCustomerType: boolean;
  props?: string[];
  select?: SelectFn<Input['state']>;
  parse?: ParseFn<Input['state']>;
  excludeKeys?: string[];
}) {
  return signalStoreFeature(
    withMethods(store => {
      const envService = config.showPortalCustomerType ? inject(EnvironmentService) : undefined;
      function getEffectivePrefix(): string {
        if (config.showPortalCustomerType && envService) {
          const portalCustomerType = envService.getPortalCustomerType();
          return `${config.prefix}_${portalCustomerType}_${config.storageKey}`;
        }
        return `${config.prefix}_${config.storageKey}`;
      }

      function clearStorage(clearStore = true): void {
        if (!config.storage) return;
        const state = getState(store);
        let toClear: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toClear = config.select(state);
        } else if (config.props) {
          toClear = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toClear = state;
        }
        // clear relevant keys from storage
        const prefix = getEffectivePrefix();
        for (const key of Object.keys(toClear)) {
          if (config.excludeKeys?.includes(key)) continue;
          const storageKey = `${prefix}_${key}`;
          config.storage.removeItem(storageKey);
          toClear[key] = undefined;
        }
        if (clearStore) {
          patchState(store, toClear);
        }
      }

      function persistStorage(): void {
        if (!config.storage) return;
        const state = getState(store);
        let toSerialize: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toSerialize = config.select(state);
        } else if (config.props) {
          toSerialize = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toSerialize = { ...state };
        }
        const prefix = getEffectivePrefix();
        for (const [key, value] of Object.entries(toSerialize)) {
          const storageKey = `${prefix}_${key}`;
          if (value === undefined || value === null || config.excludeKeys?.includes(key)) {
            config.storage.removeItem(storageKey);
          } else {
            config.storage.setItem(storageKey, JSON.stringify(value));
          }
        }
      }

      function restoreStorage(): void {
        if (!config.storage) return;
        const state = getState(store);
        let toRestore: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toRestore = config.select(state);
        } else if (config.props) {
          toRestore = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toRestore = { ...state };
        }
        const restoredData: Partial<typeof state> = {};
        const prefix = getEffectivePrefix();
        for (const key of Object.keys(toRestore)) {
          if (config.excludeKeys?.includes(key)) continue;
          const storageKey = `${prefix}_${key}`;
          const item = config.storage.getItem(storageKey);
          if (item !== null) {
            try {
              restoredData[key] = JSON.parse(item);
            } catch {
              // ignore JSON parse errors to avoid breaking the app on unexpected data through updates
            }
          }
        }
        if (config.parse) {
          const parsedData = config.parse(restoredData);
          patchState(store, parsedData);
        } else {
          patchState(store, restoredData);
        }
      }

      return { getEffectivePrefix, clearStorage, persistStorage, restoreStorage };
    }),
    withHooks({
      onInit: store => {
        store.restoreStorage();
        watchState(store, () => {
          store.persistStorage();
        });
      },
    }),
  );
}
